import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, WheelEvent as ReactWheelEvent } from 'react';
import { DoorLockIcon, PlaneCrashIcon } from './mapIcons';
import type { MapConfig, MapPoint } from '../types';
import './TacticalMap.css';

const TILE_SIZE = 256;    // px, standard tile edge length
const MIN_SCALE = 0.25;
const MAX_SCALE = 6;

// --- registry of maps. Only Chernarus has real marker data right now;
// add Livonia/Sakhal here once you have their tiles + coordinate data. ---
//
// tileUrlTemplate: your own tile URL, e.g.
//   'https://your-cdn.example.com/tiles/chernarus/{z}/{x}/{y}.webp'
// Leave it '' until you have real tiles — the map shows a labeled
// placeholder grid instead of erroring.
// minZoom/maxZoom: how many zoom levels your tile set has (0 = whole
// map in one tile, each level up doubles resolution in each axis).
export const MAPS: Record<string, MapConfig> = {
  chernarus: {
    label: 'Chernarus',
    size: 15360,
    available: true,
    tileUrlTemplate: '/tiles/chernarus/{z}/{x}/{y}.webp',
    minZoom: 1,
    maxZoom: 2,
  },
  livonia: {
    label: 'Livonia — скоро',
    size: 12800,
    available: false,
    tileUrlTemplate: '',
    minZoom: 0,
    maxZoom: 5,
  },
  sakhal: {
    label: 'Sakhal — скоро',
    size: 25600,
    available: false,
    tileUrlTemplate: '',
    minZoom: 0,
    maxZoom: 5,
  },
};

function worldToPx(x: number, z: number, mapSize: number, canvas: number) {
  return {
    px: (x / mapSize) * canvas,
    py: canvas - (z / mapSize) * canvas,
  };
}

interface TileProps {
  src: string;
  left: number;
  top: number;
  size: number;
  z: number;
  tx: number;
  ty: number;
}

/**
 * Single tile. Falls back to a labeled placeholder box if the image
 * 404s / fails to load, so you can see the grid working before real
 * tiles are in place.
 */
function Tile({ src, left, top, size, z, tx, ty }: TileProps) {
  const [missing, setMissing] = useState(false);
  return (
    <div
      className={`tac-tile${missing ? ' tac-tile-missing' : ''}`}
      style={{ left, top, width: size, height: size }}
    >
      {missing ? (
        <span className="tac-tile-label">{z}/{tx}/{ty}</span>
      ) : (
        <img src={src} alt="" onError={() => setMissing(true)} />
      )}
    </div>
  );
}

interface Viewport {
  scale: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface TileLayerProps {
  tileUrlTemplate: string;
  minZoom: number;
  maxZoom: number;
  canvas: number;
  viewport: Viewport | null;
}

/**
 * Renders only the tiles inside the current viewport (virtualized —
 * this is what makes gigapixel map images fast: at any moment there
 * are only ever a handful of <img> tags in the DOM).
 */
function TileLayer({ tileUrlTemplate, minZoom, maxZoom, canvas, viewport }: TileLayerProps) {
  if (!tileUrlTemplate) {
    return (
      <div className="tac-map-placeholder">
        Впишите tileUrlTemplate в MAPS (TacticalMap.tsx)
        <small>формат: .../{'{z}'}/{'{x}'}/{'{y}'}.webp — так же, как у xam.nu/iZurvive</small>
      </div>
    );
  }
  if (!viewport) return null;

  const { scale, x, y, width, height } = viewport;
  const z = Math.max(minZoom, Math.min(maxZoom, Math.round(maxZoom + Math.log2(scale))));
  const tileWorldSize = TILE_SIZE * Math.pow(2, maxZoom - z);

  const minWX = Math.max(0, (0 - x) / scale);
  const minWY = Math.max(0, (0 - y) / scale);
  const maxWX = Math.min(canvas, (width - x) / scale);
  const maxWY = Math.min(canvas, (height - y) / scale);

  const maxIndex = Math.pow(2, z) - 1;
  const tx0 = Math.max(0, Math.floor(minWX / tileWorldSize));
  const tx1 = Math.min(maxIndex, Math.ceil(maxWX / tileWorldSize) - 1);
  const ty0 = Math.max(0, Math.floor(minWY / tileWorldSize));
  const ty1 = Math.min(maxIndex, Math.ceil(maxWY / tileWorldSize) - 1);
  if (tx1 < tx0 || ty1 < ty0) return null;

  const tiles: { tx: number; ty: number }[] = [];
  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) {
      tiles.push({ tx, ty });
    }
  }

  return (
    <>
      {tiles.map(({ tx, ty }) => (
        <Tile
          key={`${z}-${tx}-${ty}`}
          src={tileUrlTemplate.replace('{z}', String(z)).replace('{x}', String(tx)).replace('{y}', String(ty))}
          left={tx * tileWorldSize}
          top={ty * tileWorldSize}
          size={tileWorldSize}
          z={z}
          tx={tx}
          ty={ty}
        />
      ))}
    </>
  );
}

interface Popup {
  x: number;
  y: number;
  kind: 'crash' | 'loot';
  name: string;
  wx: number;
  wz: number;
}

interface TacticalMapProps {
  mapId?: string;
  crashes?: MapPoint[];
  loot?: MapPoint[];
  showCrashes?: boolean;
  showLoot?: boolean;
}

/**
 * TacticalMap
 * props:
 *   mapId        - key into MAPS (default 'chernarus')
 *   crashes      - [{id,name,x,z}]
 *   loot         - [{id,name,x,z}]
 *   showCrashes  - boolean
 *   showLoot     - boolean
 */
export default function TacticalMap({
  mapId = 'chernarus',
  crashes = [],
  loot = [],
  showCrashes = true,
  showLoot = true,
}: TacticalMapProps) {
  const mapConfig = MAPS[mapId] || MAPS.chernarus;
  const mapSize = mapConfig.size;
  const minZoom = mapConfig.minZoom ?? 0;
  const maxZoom = mapConfig.maxZoom ?? 5;
  const canvas = useMemo(() => TILE_SIZE * Math.pow(2, maxZoom), [maxZoom]);

  const stageRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const transformRef = useRef({ scale: 1, x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, ox: 0, oy: 0 });

  const [zoomPct, setZoomPct] = useState(100);
  const [cursorWorld, setCursorWorld] = useState<{ x: number; z: number } | null>(null);
  const [popup, setPopup] = useState<Popup | null>(null);
  const [viewport, setViewport] = useState<Viewport | null>(null); // snapshot consumed by TileLayer

  const applyTransform = useCallback(() => {
    const { scale, x, y } = transformRef.current;
    if (layerRef.current) {
      layerRef.current.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    }
    setZoomPct(Math.round(scale * 100));
  }, []);

  // Snapshot the current pan/zoom + stage size for the (virtualized) tile
  // layer. Deliberately NOT called on every mousemove during a drag —
  // only at the discrete points below — so panning stays smooth and we
  // don't re-render the tile list 60x/second.
  const snapshotViewport = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const r = stage.getBoundingClientRect();
    const t = transformRef.current;
    setViewport({ scale: t.scale, x: t.x, y: t.y, width: r.width, height: r.height });
  }, []);

  const fitToStage = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const r = stage.getBoundingClientRect();
    const pad = 60;
    const scale = Math.min((r.width - pad) / canvas, (r.height - pad) / canvas);
    transformRef.current = {
      scale,
      x: (r.width - canvas * scale) / 2,
      y: (r.height - canvas * scale) / 2,
    };
    applyTransform();
    snapshotViewport();
  }, [applyTransform, snapshotViewport, canvas]);

  useEffect(() => {
    fitToStage();
    window.addEventListener('resize', fitToStage);
    return () => window.removeEventListener('resize', fitToStage);
  }, [fitToStage, mapId]);

  const zoomBy = useCallback((factor: number, center?: { x: number; y: number }) => {
    const stage = stageRef.current;
    if (!stage) return;
    const r = stage.getBoundingClientRect();
    const cx = center ? center.x - r.left : r.width / 2;
    const cy = center ? center.y - r.top : r.height / 2;
    const t = transformRef.current;
    const worldX = (cx - t.x) / t.scale;
    const worldY = (cy - t.y) / t.scale;
    const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, t.scale * factor));
    transformRef.current = {
      scale: newScale,
      x: cx - worldX * newScale,
      y: cy - worldY * newScale,
    };
    applyTransform();
    snapshotViewport();
  }, [applyTransform, snapshotViewport]);

  const onWheel = useCallback((e: ReactWheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    zoomBy(e.deltaY < 0 ? 1.15 : 1 / 1.15, { x: e.clientX, y: e.clientY });
  }, [zoomBy]);

  const onMouseDown = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    setPopup(null);
    dragRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      ox: transformRef.current.x,
      oy: transformRef.current.y,
    };
    stageRef.current?.classList.add('dragging');
  }, []);

  const updateCursor = useCallback((e: MouseEvent) => {
    const stage = stageRef.current;
    if (!stage) return;
    const r = stage.getBoundingClientRect();
    const mx = e.clientX - r.left;
    const my = e.clientY - r.top;
    const t = transformRef.current;
    const worldX = (mx - t.x) / t.scale;
    const worldY = (my - t.y) / t.scale;
    if (worldX < 0 || worldY < 0 || worldX > canvas || worldY > canvas) {
      setCursorWorld(null);
      return;
    }
    setCursorWorld({
      x: (worldX / canvas) * mapSize,
      z: mapSize - (worldY / canvas) * mapSize,
    });
  }, [mapSize, canvas]);

  const onMouseMove = useCallback((e: MouseEvent) => {
    updateCursor(e);
    const d = dragRef.current;
    if (!d.dragging) return;
    transformRef.current = {
      ...transformRef.current,
      x: d.ox + (e.clientX - d.startX),
      y: d.oy + (e.clientY - d.startY),
    };
    applyTransform();
  }, [applyTransform, updateCursor]);

  const onMouseUp = useCallback(() => {
    const wasDragging = dragRef.current.dragging;
    dragRef.current.dragging = false;
    stageRef.current?.classList.remove('dragging');
    if (wasDragging) snapshotViewport();
  }, [snapshotViewport]);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  const onMarkerClick = useCallback((e: ReactMouseEvent<HTMLDivElement>, kind: 'crash' | 'loot', point: MapPoint) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setPopup({
      x: rect.left + rect.width / 2,
      y: rect.top,
      kind,
      name: point.name,
      wx: point.x,
      wz: point.z,
    });
  }, []);

  return (
    <div className="tac-stage" ref={stageRef} onWheel={onWheel} onMouseDown={onMouseDown}>
      <div className="tac-stage-bg" />

      <div
        className="tac-map-layer"
        ref={layerRef}
        style={{ width: canvas, height: canvas }}
      >
        <TileLayer
          tileUrlTemplate={mapConfig.tileUrlTemplate}
          minZoom={minZoom}
          maxZoom={maxZoom}
          canvas={canvas}
          viewport={viewport}
        />

        <div className="tac-compass">N ▲ &nbsp; {mapSize} × {mapSize} m</div>

        {showCrashes && crashes.map((p) => {
          const { px, py } = worldToPx(p.x, p.z, mapSize, canvas);
          return (
            <div
              key={p.id}
              className="tac-marker crash"
              style={{ left: px, top: py }}
              onClick={(e) => onMarkerClick(e, 'crash', p)}
            >
              <div className="tac-diamond" />
              <PlaneCrashIcon />
            </div>
          );
        })}

        {showLoot && loot.map((p) => {
          const { px, py } = worldToPx(p.x, p.z, mapSize, canvas);
          return (
            <div
              key={p.id}
              className="tac-marker loot"
              style={{ left: px, top: py }}
              onClick={(e) => onMarkerClick(e, 'loot', p)}
            >
              <div className="tac-diamond" />
              <DoorLockIcon />
            </div>
          );
        })}
      </div>

      <div className="tac-zoom-controls">
        <button onClick={() => zoomBy(1.3)}>+</button>
        <button onClick={() => zoomBy(1 / 1.3)}>−</button>
        <button onClick={fitToStage} title="Сбросить вид">⤾</button>
      </div>

      <div className="tac-hud">
        <span>X <b>{cursorWorld ? cursorWorld.x.toFixed(0) : '—'}</b></span>
        <span className="tac-sep">/</span>
        <span>Z <b>{cursorWorld ? cursorWorld.z.toFixed(0) : '—'}</b></span>
        <span className="tac-sep">/</span>
        <span>ZOOM <b>{zoomPct}%</b></span>
      </div>

      {popup && (
        <div
          className={`tac-popup k-${popup.kind}`}
          style={{ left: popup.x, top: popup.y }}
        >
          <div className="tac-popup-kind">
            {popup.kind === 'crash' ? 'Крушение самолёта' : 'Контейнер с замком'}
          </div>
          <div className="tac-popup-name">{popup.name}</div>
          <div className="tac-popup-coords">
            X <b>{popup.wx.toFixed(1)}</b> &nbsp; Z <b>{popup.wz.toFixed(1)}</b>
          </div>
        </div>
      )}
    </div>
  );
}
